import { useState, useEffect } from 'react';
import { Modal, View, TextInput, Switch, ScrollView, KeyboardAvoidingView, Platform, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, Body } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type ImapSettingsModalProps = {
  visible: boolean;
  onClose: () => void;
  onSave: (settings: {
    host: string;
    username: string;
    password: string;
    port: number;
    secure: boolean;
    mailbox: string;
  }) => Promise<void> | void;
  defaultValues?: Partial<{
    host: string;
    username: string;
    port: number;
    secure: boolean;
    mailbox: string;
  }>;
};

export function ImapSettingsModal({
  visible,
  onClose,
  onSave,
  defaultValues,
}: ImapSettingsModalProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const [host, setHost] = useState(defaultValues?.host || 'imap.mail.me.com');
  const [username, setUsername] = useState(defaultValues?.username || '');
  const [password, setPassword] = useState('');
  const [port, setPort] = useState(String(defaultValues?.port || 993));
  const [secure, setSecure] = useState(defaultValues?.secure ?? true);
  const [mailbox, setMailbox] = useState(defaultValues?.mailbox || 'INBOX');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ host?: string; username?: string; password?: string }>({});
  const [passwordVisible, setPasswordVisible] = useState(false);

  useEffect(() => {
    if (visible) {
      setHost(defaultValues?.host || 'imap.mail.me.com');
      setUsername(defaultValues?.username || '');
      setPassword('');
      setPort(String(defaultValues?.port || 993));
      setSecure(defaultValues?.secure ?? true);
      setMailbox(defaultValues?.mailbox || 'INBOX');
      setPasswordVisible(false);
      setErrors({});
    }
  }, [visible, defaultValues?.host, defaultValues?.username, defaultValues?.port, defaultValues?.secure, defaultValues?.mailbox]);

  const handleSave = async () => {
    const nextErrors: { host?: string; username?: string; password?: string } = {};
    if (!host.trim()) {
      nextErrors.host = 'Enter your IMAP host.';
    }
    if (!username.trim()) {
      nextErrors.username = 'Enter your email address.';
    }
    if (!password.trim()) {
      nextErrors.password = 'Enter your app password.';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setSaving(true);
    try {
      await onSave({
        host: host.trim(),
        username: username.trim(),
        password: password.trim(),
        port: Number(port) || 993,
        secure,
        mailbox: mailbox.trim() || 'INBOX',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'height' : undefined}
        keyboardVerticalOffset={0}
      >
        <View
          style={{
            flex: 1,
            backgroundColor: theme.colors.overlay,
            justifyContent: 'center',
            padding: theme.spacing(2),
          }}
        >
          <Card
            variant="elevated"
            style={{
              maxHeight: '90%',
            }}
          >
          <ScrollView
            contentContainerStyle={{
              padding: theme.spacing(2),
              gap: theme.spacing(1.5),
              paddingBottom: theme.spacing(2) + insets.bottom,
            }}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
            automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
          >
          <ThemedText variant="h3" style={{ fontSize: 18 }}>
            Connect iCloud / IMAP
          </ThemedText>
          <Body color={theme.colors.textSecondary} style={{ fontSize: 13 }}>
            Enter your mailbox credentials. Use an app-specific password for iCloud.
          </Body>
          <TextInput
            placeholder="IMAP Host"
            value={host}
            onChangeText={(value) => {
              setHost(value);
              if (errors.host) setErrors((prev) => ({ ...prev, host: undefined }));
            }}
            accessibilityLabel="IMAP host"
            accessibilityHint="Enter your email provider IMAP host name"
            style={{
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
              borderRadius: theme.radii.md,
              padding: theme.spacing(1),
              color: theme.colors.text,
              backgroundColor: theme.colors.surface,
            }}
            placeholderTextColor={theme.colors.textSecondary}
          />
          {errors.host ? (
            <Body color={theme.colors.error} style={{ fontSize: 12, marginTop: -theme.spacing(0.75) }}>
              {errors.host}
            </Body>
          ) : null}
          <TextInput
            placeholder="Username (email)"
            value={username}
            onChangeText={(value) => {
              setUsername(value);
              if (errors.username) setErrors((prev) => ({ ...prev, username: undefined }));
            }}
            autoCapitalize="none"
            accessibilityLabel="IMAP username"
            accessibilityHint="Enter the email address used for this inbox"
            style={{
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
              borderRadius: theme.radii.md,
              padding: theme.spacing(1),
              color: theme.colors.text,
              backgroundColor: theme.colors.surface,
            }}
            placeholderTextColor={theme.colors.textSecondary}
          />
          {errors.username ? (
            <Body color={theme.colors.error} style={{ fontSize: 12, marginTop: -theme.spacing(0.75) }}>
              {errors.username}
            </Body>
          ) : null}
          <View style={{ position: 'relative' }}>
            <TextInput
              placeholder="App password"
              value={password}
              onChangeText={(value) => {
                setPassword(value);
                if (errors.password) setErrors((prev) => ({ ...prev, password: undefined }));
              }}
              secureTextEntry={!passwordVisible}
              accessibilityLabel="App password"
              accessibilityHint="Enter your app-specific password"
              style={{
                borderWidth: 1,
                borderColor: theme.colors.borderMuted,
                borderRadius: theme.radii.md,
                padding: theme.spacing(1),
                paddingRight: theme.spacing(6),
                color: theme.colors.text,
                backgroundColor: theme.colors.surface,
              }}
              placeholderTextColor={theme.colors.textSecondary}
            />
            <Pressable
              onPress={() => setPasswordVisible((visible) => !visible)}
              accessibilityRole="button"
              accessibilityLabel={passwordVisible ? 'Hide app password' : 'Show app password'}
              accessibilityState={{ selected: passwordVisible }}
              hitSlop={8}
              style={({ pressed }) => ({
                position: 'absolute',
                right: theme.spacing(0.5),
                top: 0,
                bottom: 0,
                width: 44,
                alignItems: 'center',
                justifyContent: 'center',
                opacity: pressed ? 0.7 : 1,
              })}
            >
              <Ionicons
                name={passwordVisible ? 'eye-off-outline' : 'eye-outline'}
                size={20}
                color={theme.colors.textSecondary}
              />
            </Pressable>
          </View>
          {errors.password ? (
            <Body color={theme.colors.error} style={{ fontSize: 12, marginTop: -theme.spacing(0.75) }}>
              {errors.password}
            </Body>
          ) : null}
          <View
            style={{
              flexDirection: 'row',
              gap: theme.spacing(1),
            }}
          >
            <View style={{ flex: 1 }}>
              <Body color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                Port
              </Body>
              <TextInput
                value={port}
                onChangeText={setPort}
                keyboardType="number-pad"
                accessibilityLabel="IMAP port"
                accessibilityHint="Enter the IMAP port number"
                placeholderTextColor={theme.colors.textSecondary}
                style={{
                  borderWidth: 1,
                  borderColor: theme.colors.borderMuted,
                  borderRadius: theme.radii.md,
                  padding: theme.spacing(1),
                  color: theme.colors.text,
                  backgroundColor: theme.colors.surface,
                }}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Body color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                Mailbox
              </Body>
              <TextInput
                value={mailbox}
                onChangeText={setMailbox}
                accessibilityLabel="Mailbox folder"
                accessibilityHint="Enter the mailbox folder to sync, such as INBOX"
                placeholderTextColor={theme.colors.textSecondary}
                style={{
                  borderWidth: 1,
                  borderColor: theme.colors.borderMuted,
                  borderRadius: theme.radii.md,
                  padding: theme.spacing(1),
                  color: theme.colors.text,
                  backgroundColor: theme.colors.surface,
                }}
              />
            </View>
          </View>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Body color={theme.colors.textSecondary}>Use SSL</Body>
            <Switch
              value={secure}
              onValueChange={setSecure}
              accessibilityLabel="Use SSL"
              accessibilityHint="Enable secure SSL connection for IMAP"
              accessibilityState={{ checked: secure }}
            />
          </View>
          <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
            <Button
              label="Cancel"
              onPress={onClose}
              variant="ghost"
              style={{ flex: 1 }}
            />
            <Button
              label={saving ? 'Saving...' : 'Save'}
              onPress={handleSave}
              disabled={saving}
              style={{ flex: 1 }}
            />
          </View>
          </ScrollView>
          </Card>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}


